/**
=========================================================
* Argon Dashboard 2 PRO MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-mui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import Container from "@mui/material/Container";
import Fade from "@mui/material/Fade";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import PropTypes from "prop-types"; // Import PropTypes
import { useState, useEffect } from "react";

// @mui material components

// Argon Dashboard 2 PRO MUI components
import ArgonBox from "components/ArgonBox";

// Argon Dashboard 2 PRO MUI example components
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

// NewProduct page components
import Header from "layouts/ecommerce/products/new-product/components/Header";

// Images
const bgImage =
  "https://raw.githubusercontent.com/creativetimofficial/public-assets/master/argon-dashboard-pro/assets/img/profile-layout-header.jpg";

/**
 * Business page template component
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Content component to display
 * @param {string} props.title - Optional page title
 * @param {Object} props.additionalProps - Additional props to pass to the content component
 * @returns {ReactElement} Business page template
 */

function BusinessPageTemplate({ children: ContentComponent, title = "", additionalProps = {} }) {
  const [showContent, setShowContent] = useState(false);
  const [headerLoaded, setHeaderLoaded] = useState(false);
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMdScreen = useMediaQuery(theme.breakpoints.between("md", "lg"));

  useEffect(() => {
    // First mark the header as loaded
    setHeaderLoaded(true);

    // Then delay content display for a staggered animation effect
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 600); // Longer delay for more noticeable sequencing

    return () => clearTimeout(timer);
  }, []);

  return (
    <DashboardLayout>
      <Fade in={true} timeout={400} appear={true}>
        <div>
          <Header title={title} />
        </div>
      </Fade>

      <ArgonBox
        mt={4}
        mb={8}
        mx="auto" // Center the content
        sx={{
          minHeight: "80vh",
          position: "relative",
          overflow: "hidden",
          maxWidth: "100%!important", // Max width for very large screens
          width: "100%", // Full width on smaller screens
          paddingRight: "0!important",
          paddingLeft: "0!important",
          backgroundColor: "white!important",
          boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
          borderRadius: "20px",
        }}
      >
        <Container
          disableGutters={isXsScreen}
          sx={{
            maxWidth: "100%!important",
            paddingRight: "0!important",
            paddingLeft: "0!important",
            backgroundColor: "white!important",
          }}
        >
          <Fade
            in={showContent}
            timeout={800}
            style={{
              transitionDelay: headerLoaded ? "300ms" : "0ms",
            }}
          >
            <div>
              <Grid
                container
                sx={{
                  position: "relative",
                  zIndex: 2,
                  height: "100%",
                  transform: showContent ? "translateY(0)" : "translateY(20px)",
                  transition: "transform 800ms cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <Grid item xs={12} sx={{ height: "100%" }}>
                  <div
                    style={{
                      position: "relative",
                      height: "100%",
                      minHeight: isXsScreen ? "500px" : isSmScreen ? "600px" : "700px", // Responsive height
                      padding: isXsScreen ? "15px" : isSmScreen ? "20px" : "30px", // Responsive padding
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      borderRadius: "10px",
                      boxShadow:
                        "0 8px 26px -4px rgba(20, 20, 20, 0.15), 0 8px 9px -5px rgba(20, 20, 20, 0.06)",
                    }}
                  >
                    {typeof ContentComponent === "function" ? (
                      <ContentComponent {...additionalProps} />
                    ) : (
                      ContentComponent
                    )}
                  </div>
                </Grid>
              </Grid>
            </div>
          </Fade>
        </Container>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

BusinessPageTemplate.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.element]).isRequired,
  title: PropTypes.string,
  additionalProps: PropTypes.object,
};

// Define default props
BusinessPageTemplate.defaultProps = {
  title: "",
  additionalProps: {},
};

export default BusinessPageTemplate;
